import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Grid, makeStyles, useTheme } from '@material-ui/core'
import PropTypes from 'prop-types'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import isEmpty from 'lodash.isempty'
import dayjs from 'dayjs'
import ListCard from '../../../../molecules/ListCard'
import Select from '../../../../molecules/Select'
import { capitalizeFirstLetter, feeLimitsDetailFormatter, numberToBPS, numberToUSD, pluralize } from '../../../../../utils'
import Text from '../../../../atoms/Text'
import FeeOverridesList from '../../FeeOverrides'
import MenuItem from '../../ClientDetailsView/MenuItem'
import { useFeeSchedule } from '../../../../../api/billing'
import Skeleton from '../../../../atoms/Skeleton'
import SelectWithSearch from '../../../../molecules/Select/SelectWithSearch'
import TextInput from '../../../../molecules/TextInput'
import { FEE_LEVEL_OVERRIDE_OPTIONS, FEE_OVERRIDE_LEVEL_VALUES, findOverrideOption } from '../../FeeOverrides/helpers'
import { messageToUse } from '../../common'
import { useBoolean, useCheckPolicy, useSearchAccountsDebounced } from '../../../../../hooks'
import RemoveButton from '../../FeeOverrides/RemoveButton'
import { FEE_CALC_TYPES, TEXT_VARIANTS } from '../../../../../constants'
import { ACCOUNTS } from '../../../../../policies/admin'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import { ACCOUNT_FORM_NAMES, mapFeeScheduleOptions, saveAccountBillingInfo } from '../helpers'
import SubmitButtons from '../SubmitButtons'
import BillingStartDateInput from '../BillingStartDateInput'
import SectionHeader from '../../shared/SectionHeader'
import EditButton from '../../shared/EditButton'
import { useAccountFormContext } from '../AccountFormProvider'

const useStyles = makeStyles((theme) => ({
  container: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  spaceDivider: {
    minHeight: '1rem',
    width: '100%'
  },
  detailsContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  itemRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  feeDetail: {
    display: 'flex',
    fontWeight: 400,
    flexDirection: 'row'
  },
  billingContainer: {
    padding: '1.7rem 1.5rem'
  }
}))

export const mapFormDefaultValues = (account) => {
  if (!account) return null

  const { billingInfo, billingStartDate = {} } = account

  const feeOverrides = (billingInfo?.feeOverrides || []).map(
    ({
      feeLevelOverrideId,
      overrideLevelType,
      overrideLevelId,
      feeScheduleId,
      billingStartDate
    }) => ({
      feeLevelOverrideId,
      feeOverrideType: overrideLevelType,
      feeOverrideLevel: overrideLevelId,
      feeScheduleOverride: feeScheduleId,
      billingStartDate: billingStartDate
    })
  )

  const billingAccountId = billingInfo?.billingAccount?.accountId || ''

  return {
    [ACCOUNT_FORM_NAMES.accountId]: account.accountId,
    [ACCOUNT_FORM_NAMES.feeOverride]: feeOverrides,
    [ACCOUNT_FORM_NAMES.billingAccount]: billingAccountId,
    [ACCOUNT_FORM_NAMES.externalBillingAccount]: billingAccountId
      ? null
      : billingInfo?.billingAccount?.externalBillingAccount || billingInfo?.externalBillingAccount || '',
    [ACCOUNT_FORM_NAMES.feeScheduleType]: billingInfo?.feeSchedule?.feeScheduleId || '',
    [ACCOUNT_FORM_NAMES.billingStartDate]: billingStartDate ? dayjs.utc(billingStartDate).format('MM/DD/YY') : null
  }
}

const AccountBillingDetails = ({
  disabled,
  filterAccountsByAssignedToClientIds,
  onSave
}) => {
  const { account, cancelEdit, ...accountForm } = useAccountFormContext()
  const sectionEditing = accountForm.sectionEditing('account_billing')
  const editMode = sectionEditing
  const theme = useTheme()
  const classes = useStyles()
  const [isSelectSearchBarFocused, setIsSelectSearchBarFocused] = useBoolean()
  const [formLoading, setFormLoading] = useBoolean()
  const [isExternalBillingAccount, setIsExternalBillingAccount] = useState(Boolean(!account.billingAccountId && account.externalBillingAccount))
  const [saveError, setSaveError] = useState(null)
  const userCanViewBilling = useCheckPolicy(ACCOUNTS.viewAccountBilling)
  const userCanEditBilling = useCheckPolicy(ACCOUNTS.editAccountBilling)

  const defaultValues = useMemo(() => {
    return mapFormDefaultValues(account)
  }, [account])

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {}
  })

  const {
    setValue,
    handleSubmit,
    reset,
    watch,
    register,
    control,
    formState: { errors, defaultValues: formDefaultValue, isValid }
  } = formMethods

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  const feeScheduleTypeValue = watch(ACCOUNT_FORM_NAMES.feeScheduleType)
  const billingAccountValue = watch(ACCOUNT_FORM_NAMES.billingAccount)
  const externalBillingAccountValue = watch(ACCOUNT_FORM_NAMES.externalBillingAccount)

  const searchAccountParams = useMemo(() => {
    return {
      ...(formDefaultValue?.billingAccount && { defaultAccountIds: [formDefaultValue?.billingAccount] })
    }
  }, [formDefaultValue.billingAccount])

  const {
    options: accountOptions,
    defaultOptions: defaultAccountOptions,
    isLoading: isLoadingAccountOptions,
    onChangeQuery,
    shouldFetchDefaultOptions
  } = useSearchAccountsDebounced(searchAccountParams)

  const selectedAccount = useMemo(() => {
    return defaultAccountOptions?.find((row) => row.value === formDefaultValue?.billingAccount) ?? {}
  }, [defaultAccountOptions, formDefaultValue?.billingAccount])

  const onSubmit = useCallback(
    async (values) => {
      try {
        setSaveError(null)
        setFormLoading.on()
        if (isNaN(parseInt(values.billingAccount))) {
          values.billingAccount = null
        }
        if (isExternalBillingAccount) {
          values.billingAccount = null
        } else if (values.billingAccount) {
          values.externalBillingAccount = accountOptions.find((row) => row.value === values.billingAccount)?.payload?.account?.accountNumber ?? ''
        }
        if (!values.externalBillingAccount?.length) {
          values.externalBillingAccount = null
        }
        await saveAccountBillingInfo({ account: values, accountDefaultValues: formDefaultValue })
        onSave()
        cancelEdit()
      } catch (err) {
        setSaveError('Issue saving changes to account billing details.  Please try again.')
        console.error(err)
      } finally {
        setFormLoading.off()
      }
    },
    [
      setFormLoading,
      isExternalBillingAccount,
      formDefaultValue,
      onSave,
      cancelEdit,
      accountOptions
    ]
  )

  const { onChangeBillingAccount, onChangeFeeScheduleType } = useMemo(() => {
    return Object.values(ACCOUNT_FORM_NAMES).reduce(
      (acc, name) => ({
        ...acc,
        [`onChange${capitalizeFirstLetter(name)}`]: (value) => {
          setValue(name, value, { shouldDirty: true })
        }
      }),
      {}
    )
  }, [setValue])

  const accountOptionsRenderer = useCallback(
    ({ payload: account }, onChange) => {
      const item = { ...account, description: account.shortNumber }
      return <MenuItem showAvatar item={item} onChange={onChange} />
    },
    []
  )

  const {
    data: feeScheduleOptions = [],
    isLoading: isLoadingFeeScheduleOptions
  } = useFeeSchedule(mapFeeScheduleOptions)

  const selectedFeeScheduleDetails = useMemo(() => {
    const feeScheduleOverride = feeScheduleOptions.find(
      ({ value }) => value === feeScheduleTypeValue
    )
    if (!feeScheduleOverride) return null

    const feeSchedule = feeScheduleOverride.payload

    return (
      <div className={classes.container}>
        {feeSchedule.details.map((feeScheduleDetails) => {
          return feeScheduleDetails.map((detail, index) => {
            const showTier = detail.calcTypeId === FEE_CALC_TYPES.TIERED_SCHEDULE || detail.calcTypeId === FEE_CALC_TYPES.FLOATING_RATE
            const isDollarRate = detail.calcTypeId === FEE_CALC_TYPES.FLAT_DOLLAR
            const rateNumber = isDollarRate ? detail.rate : numberToBPS(detail.rate)
            const denomination = detail.denomination.toUpperCase()
            const feeDescription = isDollarRate
              ? `${numberToUSD(rateNumber)} ${pluralize(rateNumber, denomination, 'S')}`
              : `${rateNumber} ${denomination}`

            return (
              <div
                key={`fee-${feeSchedule.id}-${index}`}
                className={classes.feeDetail}
              >
                {
                  showTier &&
                    <Text
                      text={feeLimitsDetailFormatter(
                        detail.limits.lower,
                        detail.limits.upper
                      )}
                      customFontWeight='bold'
                    />
                }
                &nbsp;
                <Text
                  text={feeDescription}
                />
              </div>
            )
          })
        })}
      </div>
    )
  }, [
    classes.container,
    classes.feeDetail,
    feeScheduleOptions,
    feeScheduleTypeValue
  ])

  const levelOverrideOptions = useMemo(() => {
    const assetClassOption = findOverrideOption(FEE_OVERRIDE_LEVEL_VALUES.class)
    const accountOption = findOverrideOption(FEE_OVERRIDE_LEVEL_VALUES.account)
    const positionOption = findOverrideOption(FEE_OVERRIDE_LEVEL_VALUES.position)
    return [
      ...FEE_LEVEL_OVERRIDE_OPTIONS.filter(
        ({ value }) =>
          value !== accountOption.value && value !== positionOption.value && value !== assetClassOption.value
      ),
      {
        ...assetClassOption,
        rules: {
          ...assetClassOption.rules,
          searchQueryOptions: {
            query: {
              filters: { classificationTagTypeId: 51 }
            }
          }
        }
      },
      {
        ...positionOption,
        rules: {
          ...positionOption.rules,
          searchQueryOptions: {
            queryOptions: {
              levelType: 'account',
              levelId: defaultValues.accountId
            },
            defaultPositionId: defaultValues?.feeOverride?.map(feeOverride => feeOverride.feeOverrideLevel)
          }
        }
      },
      {
        ...accountOption,
        rules: {
          max: 1,
          readOnlyValue: true,
          defaultValueIfSelected: defaultValues.accountId,
          searchQueryOptions: {
            assignedToClientIds: filterAccountsByAssignedToClientIds,
            defaultAccountIds: [defaultValues.accountId]
          }
        }
      }
    ]
  }, [defaultValues, filterAccountsByAssignedToClientIds])

  const feeScheduleOptionsRenderer = useCallback(
    ({ payload: feeSchedule }, onChange, checkedIcon) => {
      const { id, name, description } = feeSchedule
      const item = { id, name, description }
      return <MenuItem item={item} checked={checkedIcon} onChange={onChange} />
    },
    []
  )

  const feeScheduleSelectProps = useMemo(
    () => ({
      options: feeScheduleOptions,
      isLoading: isLoadingFeeScheduleOptions,
      optionsRenderer: feeScheduleOptionsRenderer
    }),
    [
      feeScheduleOptions,
      feeScheduleOptionsRenderer,
      isLoadingFeeScheduleOptions
    ]
  )

  const onResetForm = useCallback(() => {
    reset(mapFormDefaultValues(account))
  }, [account, reset])

  const onCancel = useCallback(() => {
    onResetForm()
    cancelEdit()
  }, [onResetForm, cancelEdit])

  return (
    <FadeIn className={classes.container}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <>
            <SectionHeader text='Billing Details'>
              <EditButton
                policy={ACCOUNTS.editAccountBilling}
                editing={sectionEditing}
                onClick={() => accountForm.editSection({
                  section: 'account_billing'
                })}
              />
            </SectionHeader>
            <div className={classes.spaceDivider} />
            {saveError && (
              <Text
                text={saveError}
                color={theme.palette.error.main}
                className={classes.confirmError}
              />
            )}
            <Grid item xs={12} sm={4}>
              <BillingStartDateInput
                account={account}
                onSave={(val) => { account.billingStartDate = val }}
                disableEdit={editMode}
              />
            </Grid>
            {userCanViewBilling && (
              <>
                <ListCard
                  title='Billing Account'
                  noPadding
                  disableContainerHighlight={!editMode || disabled}
                >
                  <div className={classes.detailsContent}>
                    <Grid
                      container
                      alignItems='center'
                      className={classes.billingContainer}
                    >
                      {editMode && userCanEditBilling ? (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Select
                              disabled={disabled}
                              name='isExternalBillingAccount'
                              readOnly={!editMode}
                              value={isExternalBillingAccount}
                              onChange={setIsExternalBillingAccount}
                              options={[
                                { value: false, label: 'Add Account From Summit' },
                                { value: true, label: 'Add External Billing Account' }
                              ]}
                              showCheckMarOnSelectedItems
                            />
                          </Grid>

                          {isExternalBillingAccount ? (
                            <Grid item xs={12} sm={6}>
                              <Controller
                                name={ACCOUNT_FORM_NAMES.externalBillingAccount}
                                render={() => (
                                  <TextInput
                                    fullWidth
                                    name={ACCOUNT_FORM_NAMES.externalBillingAccount}
                                    placeholder='Enter account number'
                                    helperText='50 max characters'
                                    error={Boolean(errors[ACCOUNT_FORM_NAMES.externalBillingAccount])}
                                    {...register(ACCOUNT_FORM_NAMES.externalBillingAccount, {
                                      minLength: 1,
                                      maxLength: 50
                                    })}
                                  />)}
                                control={control}
                              />
                              {Boolean(errors[ACCOUNT_FORM_NAMES.externalBillingAccount]) && (
                                <Text
                                  text={messageToUse(errors[ACCOUNT_FORM_NAMES.externalBillingAccount])}
                                  color={theme.palette.error.main}
                                  className={classes.confirmError}
                                />
                              )}
                            </Grid>
                          ) : (
                            <Grid item xs={12} sm={6}>
                              {(
                                isLoadingAccountOptions &&
                            shouldFetchDefaultOptions &&
                            !isSelectSearchBarFocused
                              )
                                ? (
                                  <Skeleton height='2.5rem' width='80%' />
                                ) : (
                                  <>
                                    <div className={classes.itemRow}>
                                      <SelectWithSearch
                                        disabled={disabled}
                                        readOnly={!editMode}
                                        options={accountOptions}
                                        value={billingAccountValue}
                                        defaultValue={billingAccountValue}
                                        defaultOptions={defaultAccountOptions}
                                        isLoading={isLoadingAccountOptions}
                                        onBlur={setIsSelectSearchBarFocused.off}
                                        onFocus={setIsSelectSearchBarFocused.on}
                                        placeholder='Select Account'
                                        onQueryChange={onChangeQuery}
                                        onChange={onChangeBillingAccount}
                                        optionsRenderer={accountOptionsRenderer}
                                        searchPlaceholder='Search by account name or number'
                                        error={errors?.billingAccount?.message}
                                      />
                                      {Boolean(billingAccountValue) && editMode && (
                                        <RemoveButton onClick={() => onChangeBillingAccount(null)} />
                                      )}
                                    </div>
                                    {errors[ACCOUNT_FORM_NAMES.billingAccount]?.message && (
                                      <Text
                                        text={errors[ACCOUNT_FORM_NAMES.billingAccount]?.message}
                                        color={theme.palette.error.main}
                                        className={classes.confirmError}
                                      />
                                    )}
                                  </>
                                )}
                            </Grid>
                          )}
                        </>
                      ) : (
                        <Grid padding='0.7rem 0.5rem' xs={12}>
                          <Box sx={{ px: '1rem' }}>
                            {isEmpty(account?.billingInfo)
                              ? <Skeleton height='2.5rem' width='100%' />
                              : (billingAccountValue || externalBillingAccountValue) ? (
                                <Text
                                  variant={TEXT_VARIANTS.h3}
                                  text={externalBillingAccountValue?.length
                                    ? 'External Billing Account: ' + externalBillingAccountValue
                                    : 'Billing Account: ' + selectedAccount?.label}
                                />
                              ) : (
                                <Text variant={TEXT_VARIANTS.h3} text='No Billing Account' />
                              )}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </div>
                </ListCard>
                <div className={classes.spaceDivider} />

                <ListCard
                  title='Fee Schedule'
                  disableContainerHighlight={!editMode || disabled}
                >
                  <div className={classes.detailsContent}>
                    {isLoadingFeeScheduleOptions ? (
                      <Skeleton height='2.5rem' width='100%' />
                    ) : (
                      <div className={classes.itemRow}>
                        <Select
                          disabled={disabled}
                          placeholder='Select Fee Schedule'
                          readOnly={!editMode || !userCanEditBilling}
                          value={feeScheduleTypeValue}
                          onChange={onChangeFeeScheduleType}
                          options={feeScheduleOptions}
                          optionsRenderer={feeScheduleOptionsRenderer}
                          error={errors?.feeScheduleType?.message}
                          showCheckMarOnSelectedItems
                        />
                        {Boolean(feeScheduleTypeValue) && editMode && (
                          <RemoveButton onClick={() => onChangeFeeScheduleType('')} />
                        )}
                        {selectedFeeScheduleDetails}
                      </div>
                    )}
                  </div>
                </ListCard>
                <div className={classes.spaceDivider} />
                <FeeOverridesList
                  editMode={editMode && userCanEditBilling}
                  levelOverrideOptions={levelOverrideOptions}
                  feeScheduleSelectProps={feeScheduleSelectProps}
                />
                <div className={classes.spaceDivider} />
              </>
            )}
          </>
          <SubmitButtons
            editMode={editMode}
            onCancel={onCancel}
            isSubmitting={formLoading}
            isFormValid={isValid}
          />
        </form>
      </FormProvider>
    </FadeIn>
  )
}

AccountBillingDetails.propTypes = {
  account: PropTypes.object,
  disabled: PropTypes.bool,
  filterAccountsByAssignedToClientIds: PropTypes.arrayOf(PropTypes.number),
  onSave: PropTypes.func
}

AccountBillingDetails.defaultProps = {
  disabled: false,
  filterAccountsByAssignedToClientIds: []
}

export default AccountBillingDetails
